import React, { useReducer } from 'react';
import produce from 'immer';
import { css } from '@emotion/react';
import { usePoller } from '@leafygreen-ui/hooks';

// components
import Button from '@leafygreen-ui/button';
import Banner from '@leafygreen-ui/banner';
import { Body } from '@leafygreen-ui/typography';

// api
import * as api from 'js/common/services/api';

// common styles
import { bodyParagraphStyles } from 'js/common/styles/multiFactorAuth';

// types
import {
  PushOktaMfaFactor,
  PushFactorVerificationResponse,
  FactorType,
  FactorResultType,
} from 'js/common/types/accountMultiFactorAuth';

const sendButtonStyles = css({
  width: '100px',
});

const errorBannerStyles = css({
  marginBottom: '14px',
});

type VerifyPushAction =
  | { type: 'onChallenge' }
  | { type: 'onChallengeSuccess'; payload: { challengeInfo: PushFactorVerificationResponse } }
  | { type: 'onChallengeError' }
  | { type: 'onTimeout' }
  | { type: 'onReject' };

interface VerifyPushReducerState {
  isPushSent: boolean;
  challengeInfo?: PushFactorVerificationResponse;
  errorMessage: string;
}

const initialState: VerifyPushReducerState = {
  isPushSent: false,
  challengeInfo: undefined,
  errorMessage: '',
};

const verifyPushReducer = produce((draft: VerifyPushReducerState, action: VerifyPushAction) => {
  switch (action.type) {
    case 'onChallenge': {
      draft.isPushSent = true;
      draft.errorMessage = '';
      return;
    }
    case 'onChallengeSuccess': {
      const { challengeInfo } = action.payload;
      draft.challengeInfo = challengeInfo;
      draft.errorMessage = '';
      return;
    }
    case 'onChallengeError': {
      draft.isPushSent = false;
      draft.errorMessage = 'A server error has occurred. Please try again in a moment.';
      return;
    }
    case 'onTimeout': {
      draft.isPushSent = false;
      draft.challengeInfo = undefined;
      draft.errorMessage = 'Your push notification has expired.';
      return;
    }
    case 'onReject': {
      draft.isPushSent = false;
      draft.challengeInfo = undefined;
      draft.errorMessage = 'You have rejected this login.';
      return;
    }

    default:
      return draft;
  }
}, initialState);

interface VerifyPushFactorProps {
  pushFactor: PushOktaMfaFactor;
  onSuccess: () => void;
  isDeleteFlow?: boolean;
}

export default function VerifyPushFactor({ pushFactor, onSuccess, isDeleteFlow }: VerifyPushFactorProps) {
  const [{ isPushSent, challengeInfo, errorMessage }, dispatch] = useReducer(verifyPushReducer, initialState);

  const sendPushChallenge = async () => {
    dispatch({ type: 'onChallenge' });

    try {
      const response = await api.accountMultiFactorAuth.verifyFactor(
        {
          factorType: FactorType.Push,
          factorId: pushFactor.id,
        },
        isDeleteFlow
      );
      dispatch({ type: 'onChallengeSuccess', payload: { challengeInfo: response as PushFactorVerificationResponse } });
    } catch ({ errorCode }) {
      dispatch({ type: 'onChallengeError' });
    }
  };

  const pollForPushFactorVerification = async () => {
    const { transactionId } = challengeInfo as PushFactorVerificationResponse;

    try {
      const { factorResult } = await api.accountMultiFactorAuth.verifyPushFactorChallenge({
        factorId: pushFactor.id,
        transactionId,
      });

      switch (factorResult) {
        case FactorResultType.REJECTED:
          dispatch({ type: 'onReject' });
          break;
        case FactorResultType.TIMEOUT:
          dispatch({ type: 'onTimeout' });
          break;
        case FactorResultType.SUCCESS:
          onSuccess();
          break;
        default:
        // do nothing
      }
    } catch (error) {
      console.log('An error occurred during polling', error);
    }
  };

  usePoller(pollForPushFactorVerification, {
    interval: 5e3,
    enabled: !!challengeInfo,
    immediate: false,
  });

  return (
    <>
      {errorMessage && (
        <Banner variant="danger" data-testid="error-banner" css={errorBannerStyles}>
          {errorMessage}
        </Banner>
      )}

      <Body css={bodyParagraphStyles}>Send a push notification to authenticate on your device.</Body>
      <Button css={sendButtonStyles} variant="primary" disabled={isPushSent} onClick={sendPushChallenge}>
        {isPushSent ? 'Push Sent' : 'Send Push'}
      </Button>
    </>
  );
}
